import { createContext, useContext } from 'react';
import type { RichTextDoc, RichTextNode } from '@org/model';
import type { RichTextRendererProps } from './types';

const MentionRendererContext = createContext<RichTextRendererProps['renderMention']>(undefined);

export function RichTextRenderer({ value, className, renderMention }: RichTextRendererProps) {
  if (!value || !Array.isArray(value.content)) return null;
  return (
    <MentionRendererContext.Provider value={renderMention}>
      <div className={`prose prose-sm max-w-none ${className ?? ''}`}>
        {value.content.map((node, i) => <NodeView key={i} node={node} />)}
      </div>
    </MentionRendererContext.Provider>
  );
}

function NodeView({ node }: { node: RichTextNode }) {
  switch (node.type) {
    case 'paragraph':
      return <p>{node.content?.map((c, i) => <InlineView key={i} node={c} />)}</p>;
    case 'heading': {
      const rawLevel = (node.attrs?.level as number | undefined) ?? 2;
      const level = Math.min(Math.max(Number.isFinite(rawLevel) ? rawLevel : 2, 1), 6) as 1 | 2 | 3 | 4 | 5 | 6;
      const Tag = `h${level}` as 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6';
      return <Tag>{node.content?.map((c, i) => <InlineView key={i} node={c} />)}</Tag>;
    }
    case 'blockquote':
      return <blockquote>{node.content?.map((c, i) => <NodeView key={i} node={c} />)}</blockquote>;
    case 'codeBlock':
      return <pre><code>{node.content?.map((c) => c.text ?? '').join('')}</code></pre>;
    case 'bulletList':
      return <ul>{node.content?.map((c, i) => <NodeView key={i} node={c} />)}</ul>;
    case 'orderedList':
      return <ol>{node.content?.map((c, i) => <NodeView key={i} node={c} />)}</ol>;
    case 'listItem':
      return <li>{node.content?.map((c, i) => <NodeView key={i} node={c} />)}</li>;
    case 'horizontalRule':
      return <hr />;
    case 'image':
      return <img src={node.attrs?.src as string} alt={(node.attrs?.alt as string) ?? ''} loading="lazy" decoding="async" className="rounded-lg max-w-full h-auto" />;
    case 'video':
      return <video src={node.attrs?.src as string} controls preload="metadata" className="rounded-lg max-w-full" />;
    default:
      return null;
  }
}

function InlineView({ node }: { node: RichTextNode }) {
  if (node.type === 'text') {
    let content: React.ReactNode = node.text ?? '';
    for (const mark of node.marks ?? []) {
      if (mark.type === 'bold') content = <strong>{content}</strong>;
      else if (mark.type === 'italic') content = <em>{content}</em>;
      else if (mark.type === 'underline') content = <u>{content}</u>;
      else if (mark.type === 'strike') content = <s>{content}</s>;
      else if (mark.type === 'link') content = <a href={mark.attrs?.href as string} target="_blank" rel="noreferrer" className="text-primary underline">{content}</a>;
    }
    return <>{content}</>;
  }
  if (node.type === 'mention') {
    const userId = node.attrs?.userId as string;
    const label = (node.attrs?.label as string) ?? '';
    return <MentionNode userId={userId} label={label} />;
  }
  if (node.type === 'emoji') {
    return <span data-emoji={node.attrs?.shortcode as string}>{node.attrs?.unicode as string}</span>;
  }
  if (node.type === 'hardBreak') return <br />;
  return null;
}

function MentionNode({ userId, label }: { userId: string; label: string }) {
  const renderMention = useContext(MentionRendererContext);
  if (renderMention && userId) {
    return <>{renderMention({ userId, label })}</>;
  }
  return <span data-mention={userId} className="rounded bg-primary/10 text-primary px-1">@{label}</span>;
}
